import { observable, action, computed, makeObservable } from 'mobx';

/* =========================
   Types
========================= */

export type BreakpointKey = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl';

interface WindowDimensions {
    width: number;
    height: number;
}

type SideNavTheme = 'SIDE_NAV_LIGHT' | 'SIDE_NAV_DARK';
type NavType = 'NAV_TYPE_SIDE' | 'NAV_TYPE_TOP';
type Direction = 'DIR_LTR' | 'DIR_RTL';
type ThemeMode = 'light' | 'dark';

/* =========================
   Breakpoints
========================= */

const breakpoints: Record<BreakpointKey, number> = {
    xs: 480,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
    xxl: 1600,
    xxxl: 3200,
};

/* =========================
   Store
========================= */

class UIStore {
    // Observables
    errors: unknown = null;
    selectedBreakPoint: number | null = null;
    selectedKey: string[] = [];

    windowDimensions: WindowDimensions = {
        width: window.innerWidth,
        height: window.innerHeight,
    };

    responsive: boolean | null = null;
    sideNavTheme: SideNavTheme = 'SIDE_NAV_LIGHT';
    locale = 'en';
    navType: NavType = 'NAV_TYPE_SIDE';
    topNavColor = '#3e82f7';
    headerNavColor = '#fff';
    mobileNav = false;
    currentTheme: ThemeMode = 'light';
    direction: Direction = 'DIR_LTR';

    constructor() {
        makeObservable(this, {
            // Observables
            errors: observable,
            selectedBreakPoint: observable,
            selectedKey: observable,
            windowDimensions: observable,
            responsive: observable,
            sideNavTheme: observable,
            locale: observable,
            navType: observable,
            topNavColor: observable,
            headerNavColor: observable,
            mobileNav: observable,
            currentTheme: observable,
            direction: observable,

            // Actions
            onMobileNavToggle: action,
            selectBreakPoint: action,
            setSelectedKey: action,
            updateWindowDimensions: action,

            // Computed
            isMobileNavOpen: computed,
            isConstrainedViewport: computed,
            isMobileViewport: computed,
        });
    }

    /* =========================
       Actions
    ========================= */

    onMobileNavToggle(value: boolean): void {
        this.mobileNav = value;
    }

    selectBreakPoint(key: BreakpointKey): void {
        this.selectedBreakPoint = breakpoints[key];
    }

    setSelectedKey(key: string []): void {
        this.selectedKey = key;
    }

    updateWindowDimensions(width: number, height: number): void {
        this.windowDimensions.width = width;
        this.windowDimensions.height = height;

        if (this.selectedBreakPoint === null) {
            this.responsive = false;
            return;
        }

        this.responsive = width <= this.selectedBreakPoint;
    }

    /* =========================
       Computed
    ========================= */

    get isMobileNavOpen(): boolean {
        return this.mobileNav;
    }

    get isConstrainedViewport(): boolean {
        const { width, height } = this.windowDimensions;
        return width < 1440 || height < 1080;
    }

    /** Mobile / tablet-or-less: width ≤ 768px. Use for layout/scroll (e.g. Get Quote modal, parts list scroll). */
    get isMobileViewport(): boolean {
        return this.windowDimensions.width <= breakpoints.md;
    }

    /* =========================
       Helpers
    ========================= */

    getContainerHeight(): number {
        const header = 76;
        const footer = this.responsive ? 38 : 32;
        const menu = this.responsive ? 64 : 0;

        return this.windowDimensions.height - header - footer - menu;
    }

    setDocumentTitle(title: string): void {
        const prefix = 'PartsBadger';
        document.title = `${prefix} - ${title}`;
    }
}

/* =========================
   Export singleton
========================= */

const uiStore = new UIStore();
export default uiStore;
